import { Link } from 'react-router-dom';
import { streamTypes, bitrateOptions } from '@/mocks/registerRadio';
import type { RadioFormData } from '../types';

interface ReviewSummaryProps {
  formData: RadioFormData;
  setCurrentStep: (step: number) => void;
  isFree: boolean;
  isPremium: boolean;
  planName: string;
  planPriceDisplay: string;
  onAcceptTerms: (accepted: boolean) => void;
  termsError?: string;
}

export default function ReviewSummary({ formData, setCurrentStep, isFree, isPremium, planName, planPriceDisplay, onAcceptTerms, termsError }: ReviewSummaryProps) {

  return (
    <div className="space-y-5">
      <p className="text-sm text-foreground-500">
        Revisa que todos los datos sean correctos antes de enviar el registro. También podés volver atrás y editar cualquier sección.
      </p>

      {/* Activation notice */}
      <div
        className={`rounded-xl p-4 border ${
          isFree ? 'bg-amber-50/70 border-amber-200' : 'bg-green-50/70 border-green-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
              isFree ? 'bg-amber-200' : 'bg-green-200'
            }`}
          >
            <i
              className={`text-base ${isFree ? 'ri-time-line text-amber-700' : 'ri-flashlight-line text-green-700'}`}
            ></i>
          </div>
          <div>
            <p className="text-sm font-bold text-foreground-800 mb-0.5">
              {isFree ? 'Registro sujeto a aprobación' : 'Activación inmediata con revisión posterior'}
            </p>
            <p className="text-xs text-foreground-500 leading-relaxed">
              {isFree
                ? 'Al enviar, tu solicitud será revisada por nuestro equipo. Recibirás un correo cuando tu radio esté activa. El proceso toma de 24 a 48 horas.'
                : 'Tu radio estará activa al instante. Accede al panel de administración apenas completes el registro. Nuestro equipo revisará tu contenido posteriormente.'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {/* 1. Radio info */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-4 hover:border-primary-300 transition-colors group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-radio-line text-primary-600 text-sm"></i>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-800">Datos de la radio</h4>
                <p className="text-[10px] text-foreground-400">Paso 1 de 5</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
            >
              <i className="ri-pencil-line mr-1"></i>Editar
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div>
              <p className="text-foreground-400">Nombre</p>
              <p className="text-foreground-800 font-semibold">{formData.name || '—'}</p>
            </div>
            <div>
              <p className="text-foreground-400">Categoría</p>
              <p className="text-foreground-800 font-semibold">{formData.category || '—'}</p>
            </div>
            {formData.slogan && (
              <div className="col-span-2">
                <p className="text-foreground-400">Eslogan</p>
                <p className="text-foreground-800 font-semibold">{formData.slogan}</p>
              </div>
            )}
            {formData.description && (
              <div className="col-span-2">
                <p className="text-foreground-400">Descripción</p>
                <p className="text-foreground-700 text-xs leading-relaxed line-clamp-2">{formData.description}</p>
              </div>
            )}
            <div>
              <p className="text-foreground-400">País</p>
              <p className="text-foreground-800 font-semibold">{formData.country || '—'}</p>
            </div>
            <div>
              <p className="text-foreground-400">Ciudad</p>
              <p className="text-foreground-800 font-semibold">{formData.city || '—'}</p>
            </div>
          </div>
        </div>

        {/* 2. Stream */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-4 hover:border-primary-300 transition-colors group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                <i className="ri-sound-module-line text-secondary-600 text-sm"></i>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-800">Modalidad de Streaming</h4>
                <p className="text-[10px] text-foreground-400">Paso 2 de 5</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
            >
              <i className="ri-pencil-line mr-1"></i>Editar
            </button>
          </div>
          {formData.streamingMode === 'RFW_MANAGED' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                  <i className="ri-cloud-line mr-1"></i>
                  Streaming administrado por RFW
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">
                  <i className="ri-check-double-line mr-1 text-[9px]"></i>
                  Aprovisionamiento automático
                </span>
              </div>
              <p className="text-xs text-foreground-500">
                RadioFrecuenciaWeb creará tu estación de streaming con AutoDJ, almacenamiento, playlists, monitoreo y soporte.{' '}
                <strong>No necesitás ingresar Stream URL.</strong> Recibirás tu URL pública cuando la estación esté lista.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div className="col-span-2">
                <p className="text-foreground-400">URL</p>
                <p className="text-foreground-800 font-semibold truncate font-mono text-[11px]">
                  {formData.streamUrl || '—'}
                </p>
              </div>
              <div>
                <p className="text-foreground-400">Tipo</p>
                <p className="text-foreground-800 font-semibold">
                  {streamTypes.find((t) => t.value === formData.streamType)?.label || '—'}
                </p>
              </div>
              <div>
                <p className="text-foreground-400">Bitrate</p>
                <p className="text-foreground-800 font-semibold">
                  {bitrateOptions.find((b) => b.value === formData.bitrate)?.label || '—'}
                </p>
              </div>
              {formData.providerName && (
                <div className="col-span-2">
                  <p className="text-foreground-400">Proveedor</p>
                  <p className="text-foreground-800 font-semibold">{formData.providerName}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3. Logo & branding */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-4 hover:border-primary-300 transition-colors group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-palette-line text-accent-600 text-sm"></i>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-800">Logo y branding</h4>
                <p className="text-[10px] text-foreground-400">Paso 3 de 5</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
            >
              <i className="ri-pencil-line mr-1"></i>Editar
            </button>
          </div>
          <div className="flex items-center gap-4">
            {formData.logoPreview ? (
              <img
                src={formData.logoPreview}
                alt="Logo"
                className="w-14 h-14 object-contain rounded-xl bg-background-100 border border-background-200"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-background-100 border border-dashed border-background-300 flex items-center justify-center">
                <i className="ri-image-line text-foreground-300 text-lg"></i>
              </div>
            )}
            <div className="space-y-1.5">
              <div>
                <p className="text-[10px] text-foreground-400">Estado</p>
                <p className="text-xs font-semibold text-foreground-800">
                  {formData.logoPreview ? 'Logo cargado' : 'Sin logo'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-400">Color principal</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div
                    className="w-4 h-4 rounded-full border border-background-300"
                    style={{ backgroundColor: formData.accentColor }}
                  ></div>
                  <span className="text-xs text-foreground-600 font-mono uppercase">{formData.accentColor}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Plan */}
        <div
          className={`rounded-xl p-4 border hover:border-primary-300 transition-colors group ${
            isPremium
              ? 'bg-primary-50/20 border-primary-200'
              : 'bg-background-50 border-background-200'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-vip-crown-line text-accent-600 text-sm"></i>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-800">Plan</h4>
                <p className="text-[10px] text-foreground-400">Paso 4 de 5</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
            >
              <i className="ri-pencil-line mr-1"></i>Editar
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {formData.planId ? (
              <>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    isPremium
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}
                >
                  {planName}
                </span>
                <span className="text-sm font-bold text-foreground-800">{planPriceDisplay}</span>
                <span
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold ${
                    isFree
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}
                >
                  {isFree ? 'Sujeto a aprobación' : 'Activación inmediata'}
                </span>
              </>
            ) : (
              <p className="text-sm text-red-500 font-semibold flex items-center gap-1">
                <i className="ri-error-warning-line text-xs"></i>
                No seleccionado
              </p>
            )}
          </div>
        </div>

        {/* 5. Frequency */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-4 group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-radar-line text-accent-600 text-sm"></i>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-foreground-800">Frecuencia RFW</h4>
                <p className="text-[10px] text-foreground-400">Automática al aprobarse</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-foreground-500">Asignación automática</span>
            <span className="text-xs font-bold text-foreground-400">RFW</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">
              <i className="ri-check-double-line mr-1 text-[9px]"></i>
              Al aprobarse
            </span>
          </div>
          <p className="text-[10px] text-foreground-400 mt-3 pt-2 border-t border-background-100">
            <i className="ri-information-line mr-1"></i>
            Al aprobarse tu radio, el sistema asignará automáticamente una frecuencia RFW disponible. Las frecuencias específicas o premium se solicitan posteriormente mediante pago y aprobación.
          </p>
        </div>
      </div>

      {/* ── Legal checkbox ── */}
      <div
        className={`rounded-xl p-4 border-2 transition-all ${
          termsError
            ? 'border-red-300 bg-red-50/30'
            : 'border-background-200 bg-background-50'
        }`}
      >
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative mt-0.5 shrink-0">
            <input
              type="checkbox"
              checked={formData.acceptedTerms}
              onChange={(e) => onAcceptTerms(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                formData.acceptedTerms
                  ? 'bg-primary-500 border-primary-500'
                  : 'border-background-300 group-hover:border-primary-300 bg-background-50'
              }`}
            >
              {formData.acceptedTerms && (
                <i className="ri-check-line text-background-50 text-[10px] font-bold"></i>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground-800 mb-0.5">
              Acepto términos, responsabilidad de licencias musicales
              {formData.streamingMode === 'EXTERNAL_STREAM'
                ? ' y uso de proveedor de streaming externo'
                : ' y contrato de streaming administrado por RFW'}{' '}
              <span className="text-red-500">*</span>
            </p>
            <p className="text-[11px] text-foreground-500 leading-relaxed">
              {formData.streamingMode === 'EXTERNAL_STREAM' ? (
                <>
                  Al marcar esta casilla confirmás que sos el único responsable de obtener y mantener todas las licencias musicales, derechos de autor, licencias de transmisión y permisos regulatorios necesarios para tu emisora. La radio conecta su propia Stream URL. También aceptás nuestros{' '}
                </>
              ) : (
                <>
                  Al marcar esta casilla confirmás que sos el único responsable de obtener y mantener todas las licencias musicales, derechos de autor, licencias de transmisión y permisos regulatorios necesarios para tu emisora. RadioFrecuenciaWeb administrará tu servidor de streaming según el plan contratado. También aceptás nuestros{' '}
                </>
              )}
              <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-semibold underline transition-colors">Términos y Condiciones</Link>
              ,{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-semibold underline transition-colors">Política de Privacidad</Link>
              {' '}y las{' '}
              <Link to="/radio-rules" className="text-primary-600 hover:text-primary-700 font-semibold underline transition-colors">Reglas de Aprobación</Link>
              .
            </p>
          </div>
        </label>
        {termsError && (
          <p className="text-[11px] text-red-500 mt-2 ml-8">{termsError}</p>
        )}
      </div>
    </div>
  );
}